import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { useIdentifyQueueWorker } from "@/lib/identifyQueue";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import Camera from "@/pages/Camera";
import Result from "@/pages/Result";
import Gallery from "@/pages/Gallery";
import PlantDetail from "@/pages/PlantDetail";
import Settings from "@/pages/Settings";
import About from "@/pages/About";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Duplicates from "@/pages/Duplicates";
import Share from "@/pages/Share";
import Corridor from "@/pages/Corridor";
import AdminCsp from "@/pages/AdminCsp";
import ErrorBoundary from "@/components/ErrorBoundary";
import { MilestonesProvider } from "@/contexts/MilestonesContext";

function BackgroundWorkers() {
  const { user } = useAuth();
  useIdentifyQueueWorker(user?.user_id);
  return null;
}

// Scrolls the window (and the app-shell scroll container) to the top whenever
// the route pathname changes. React Router doesn't do this by default, which
// is jarring — e.g. clicking a plant on page 3 of the gallery would open the
// Result page mid-scroll instead of at the top. `search` and `hash` changes
// are intentionally ignored so we don't re-scroll when the master-detail pane
// updates `?selected=<id>`.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // `auto` = instant, no smooth-scroll flash. Fallback for older browsers.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <ScrollToTop />
      <BackgroundWorkers />
      <Routes>
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/about" element={<About />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/share/:token" element={<ErrorBoundary><Share /></ErrorBoundary>} />
        <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
        <Route path="/camera" element={<ProtectedRoute><Camera /></ProtectedRoute>} />
        <Route path="/result" element={<ProtectedRoute><Result /></ProtectedRoute>} />
        <Route path="/gallery" element={<ProtectedRoute><Gallery /></ProtectedRoute>} />
        <Route path="/duplicates" element={<ProtectedRoute><Duplicates /></ProtectedRoute>} />
        <Route path="/plant/:id" element={<ProtectedRoute><PlantDetail /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/corridor" element={<ProtectedRoute><Corridor /></ProtectedRoute>} />
        <Route path="/admin/csp" element={<ProtectedRoute><AdminCsp /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <MilestonesProvider>
          <AppRouter />
          <Toaster position="top-center" richColors toastOptions={{ classNames: { toast: "font-body rounded-2xl" } }} />
        </MilestonesProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
